import React from 'react'
import { Card, CardContent, Typography } from '@mui/material'

// A box that owns its whole row spans both columns where it stands, rather than
// being lifted out of the grid: that way a full-width row can sit anywhere in
// the registry's order, not only at the top. Longhands, because jsdom does not
// resolve the `grid-column` shorthand and the tests need to read this back.
export const FULL_ROW = { gridColumnStart: 1, gridColumnEnd: -1 }

// The theme's paper is a light grey, which leaves a barcode's own white
// background sitting as a bright rectangle inside its card. A box can ask for
// a white surface instead so the two meet without a seam.
const WHITE = { bgcolor: 'common.white' }

// Same rule ParcelPage applies to a section: only these mean "nothing to show".
const isEmpty = (content) =>
  content === null || content === undefined || content === false

// A box has no opinion about who may see what; the parts carry their own
// criteria. Its one decision is whether anything is left to put in the card.
// Each part body is called as a plain function, like ParcelPage calls a
// section, so the bodies' hooks belong to this component. That needs the same
// parts in the same order on every render, which Box guarantees by keying this
// component on the part ids.
function BoxContent({ id, title, fullWidth, white, parts, parcel, onChanged }) {
  const shown = parts
    .map(({ id: partId, component }) => ({
      id: partId,
      content: component({ parcel, onChanged }),
    }))
    .filter(({ content }) => !isEmpty(content))

  if (shown.length === 0) return null

  return (
    <Card
      data-testid={`box-${id}`}
      sx={{ ...(fullWidth && FULL_ROW), ...(white && WHITE) }}
    >
      <CardContent>
        {title && (
          <Typography variant="h6" component="h2" gutterBottom>
            {title}
          </Typography>
        )}
        {shown.map(({ id: partId, content }) => (
          <React.Fragment key={partId}>{content}</React.Fragment>
        ))}
      </CardContent>
    </Card>
  )
}

export default function Box({
  id,
  title,
  fullWidth,
  white,
  parts,
  parcel,
  onChanged,
}) {
  // Keyed on the parcel and the part ids. The parts' local state (a half-typed
  // note, an open editor) belongs to one parcel, so another parcel gets a fresh
  // box, as ParcelPage does for its sections. Parts also come and go with the
  // parcel, and a changed set remounts the box, which resets the local state of
  // EVERY part in it, not just the ones that came or went. That is the price of
  // keeping the bodies' hook order stable.
  //
  // Part contract: a body is called as a plain function, so memo(),
  // forwardRef() and class components throw.
  const partsKey = `${parts.map((part) => part.id).join('|')}:${parcel._id}`
  return (
    <BoxContent
      key={partsKey}
      id={id}
      title={title}
      fullWidth={fullWidth}
      white={white}
      parts={parts}
      parcel={parcel}
      onChanged={onChanged}
    />
  )
}
